import { FC, useState } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { styled } from '@mui/material/styles';
import api from 'lib/trpcClient';
import AmlDetailItem from './AmlDetailItem';

const StyledContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
  borderRadius: '4px',
  border: `1px solid ${theme.palette.divider}`,
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(theme.spacingSizes.xs),
}));

const TabTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive: boolean }>(({ theme, isActive }) => ({
  fontWeight: isActive ? 500 : 400,
  color: isActive ? theme.palette.grey[900] : theme.palette.grey[500],
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  '& .MuiTabs-list': {
    gap: theme.spacing(theme.spacingSizes.sm),
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.xs, theme.spacingSizes.sm),
}));

const AmlDetailItemContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.lg, 0),
}));

enum AmlTabs {
  Pep,
  FitnessProbity,
  Warning,
  AdverseMedia,
}

type Props = {
  vehicleId: string;
  investorId: string;
  caDocId: string;
};

const AmlResultInformation: FC<Props> = ({
  vehicleId,
  investorId,
  caDocId,
}) => {
  const [amlHit] = api.lpKyc.getAmlResultInformation.useSuspenseQuery({
    vehicleId,
    investorId,
    caDocId,
  });
  const showPepTab = !!amlHit.pepDetails.length;
  const showFitnessProbityTab = !!amlHit.fitnessProbityDetails.length;
  const showWarningTab = !!amlHit.warningDetails.length;
  const showAdverseMediaTab = !!amlHit.adverseMediaDetails?.length;

  const [activeTab, setActiveTab] = useState<AmlTabs>(() => {
    if (showPepTab) {
      return AmlTabs.Pep;
    } else if (showFitnessProbityTab) {
      return AmlTabs.FitnessProbity;
    } else if (showWarningTab) {
      return AmlTabs.Warning;
    } else if (showAdverseMediaTab) {
      return AmlTabs.AdverseMedia;
    }
    return AmlTabs.Pep;
  });

  const handleTabChange = (_: unknown, selectedTab: AmlTabs) => {
    setActiveTab(selectedTab);
  };

  return (
    <StyledContainer>
      <StyledTitle variant="h5">Result information</StyledTitle>
      <StyledTabs
        value={activeTab}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        showDivider
        variant="scrollable"
      >
        {showPepTab ? (
          <StyledTab
            value={AmlTabs.Pep}
            label={
              <TabTitle variant="h6" isActive={activeTab === AmlTabs.Pep}>
                PEP
              </TabTitle>
            }
          />
        ) : null}
        {showFitnessProbityTab ? (
          <StyledTab
            value={AmlTabs.FitnessProbity}
            label={
              <TabTitle
                variant="h6"
                isActive={activeTab === AmlTabs.FitnessProbity}
              >
                Fitness Probity
              </TabTitle>
            }
          />
        ) : null}
        {showWarningTab ? (
          <StyledTab
            value={AmlTabs.Warning}
            label={
              <TabTitle variant="h6" isActive={activeTab === AmlTabs.Warning}>
                Warning
              </TabTitle>
            }
          />
        ) : null}
        {showAdverseMediaTab ? (
          <StyledTab
            value={AmlTabs.AdverseMedia}
            label={
              <TabTitle
                variant="h6"
                isActive={activeTab === AmlTabs.AdverseMedia}
              >
                Adverse Media
              </TabTitle>
            }
          />
        ) : null}
      </StyledTabs>
      <AmlDetailItemContainer>
        {activeTab === AmlTabs.Pep
          ? amlHit.pepDetails.map((entity, index) => (
              <AmlDetailItem
                key={`pep-${entity.url || entity.name}-${
                  entity.source || index
                }`}
                entity={entity}
                index={index}
                type="pep"
              />
            ))
          : null}

        {activeTab === AmlTabs.FitnessProbity
          ? amlHit.fitnessProbityDetails.map((entity, index) => (
              <AmlDetailItem
                key={`fitness-${entity.url || entity.name}-${
                  entity.source || index
                }`}
                entity={entity}
                index={index}
                type="fitness"
              />
            ))
          : null}

        {activeTab === AmlTabs.Warning
          ? amlHit.warningDetails.map((entity, index) => (
              <AmlDetailItem
                key={`warning-${entity.url || entity.name}-${
                  entity.source || index
                }`}
                entity={entity}
                index={index}
                type="warning"
              />
            ))
          : null}

        {activeTab === AmlTabs.AdverseMedia
          ? amlHit.adverseMediaDetails?.map((entity, index) => (
              <AmlDetailItem
                key={`adverse-media-${entity.url || entity.name}-${
                  entity.published ?? index
                }`}
                entity={entity}
                index={index}
                type="adverse-media"
              />
            ))
          : null}
      </AmlDetailItemContainer>
    </StyledContainer>
  );
};

export default AmlResultInformation;
